import { useEffect, useState } from "react";
import { ChevronDown, Copy, KeyRound, Loader2, Ban } from "lucide-react";
import { createPresentationKey, listPresentationKeys, revokePresentationKey } from "../../lib/api";
import type { PresentationKey } from "../../lib/types";

const VALIDITY_OPTIONS = [
  { days: 1, label: "1 Tag" },
  { days: 7, label: "7 Tage" },
  { days: 30, label: "30 Tage" },
  { days: 90, label: "90 Tage" },
];

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function keyState(k: PresentationKey): { label: string; live: boolean } {
  if (k.revoked_at) return { label: "Widerrufen", live: false };
  if (new Date(k.expires_at).getTime() <= Date.now()) return { label: "Abgelaufen", live: false };
  return { label: `Gültig bis ${formatDate(k.expires_at)}`, live: true };
}

/**
 * REASONZ Live: issue and revoke presentation keys. A key lets a self-built
 * HTML presentation (SDK at /sdk/reasonz-live.js) open/close questions and run
 * clustering + insight tools on this event. Reading results needs no key.
 */
export default function PresentationKeyCard({ eventId, joinCode }: { eventId: string; joinCode: string }) {
  const [rows, setRows] = useState<PresentationKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [label, setLabel] = useState("");
  const [days, setDays] = useState(7);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fresh, setFresh] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setRows(await listPresentationKeys(eventId));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Schlüssel konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const key = await createPresentationKey(eventId, label.trim(), days);
      setFresh(key);
      setCopied(false);
      setLabel("");
      void load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Schlüssel konnte nicht erstellt werden.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRevoke = async (row: PresentationKey) => {
    if (!window.confirm(`Schlüssel …${row.key_hint} widerrufen? Präsentationen mit diesem Schlüssel können danach nichts mehr auslösen.`)) return;
    try {
      await revokePresentationKey(row.id);
      void load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Widerrufen fehlgeschlagen.");
    }
  };

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard can be blocked; the key stays selectable in the field.
    }
  };

  return (
    <section className="rounded-2xl border border-rc-fog bg-rc-paper p-6 sm:p-7 max-w-xl">
      <header className="mb-5">
        <h3 className="text-rc-black font-bold text-[18px] tracking-[-0.01em]">Präsentations-Schlüssel</h3>
        <p className="text-rc-charcoal text-[13px] leading-[1.5] mt-1.5">
          Für eigene HTML-Präsentationen mit dem REASONZ-Live-SDK. Ergebnisse und QR-Code mit dem Code{" "}
          <strong className="font-semibold">{joinCode}</strong> funktionieren ohne Schlüssel. Der Schlüssel erlaubt
          zusätzlich, aus der Präsentation Fragen zu öffnen und KI-Auswertungen zu starten. Gib ihn beim Vortrag
          ein oder hänge ihn als <code className="text-[12px]">#rzkey=…</code> an die Adresse; schreib ihn nie in die
          HTML-Datei selbst.
        </p>
      </header>

      {fresh && (
        <div className="mb-5 rounded-xl border-[1.5px] border-rc-berry/40 bg-rc-cream p-4">
          <div className="text-[11px] font-semibold text-rc-charcoal uppercase tracking-[0.14em] mb-2">
            Neuer Schlüssel, nur jetzt sichtbar
          </div>
          <div className="flex gap-2">
            <input
              readOnly
              value={fresh}
              onFocus={(e) => e.currentTarget.select()}
              className="flex-1 min-w-0 bg-rc-paper border-[1.5px] border-rc-fog rounded-xl px-3 py-2 font-mono text-[12px] text-rc-black"
            />
            <button
              type="button"
              onClick={() => copy(fresh)}
              className="inline-flex items-center gap-1.5 rounded-pill border-[1.5px] border-rc-fog px-3 py-2 text-[13px] font-semibold text-rc-black hover:border-rc-berry"
            >
              <Copy className="w-3.5 h-3.5" />
              {copied ? "Kopiert" : "Kopieren"}
            </button>
          </div>
          <button type="button" onClick={() => setFresh(null)} className="mt-2 text-[12px] text-rc-slate hover:text-rc-berry">
            Gesichert, ausblenden
          </button>
        </div>
      )}

      {loading ? (
        <div className="py-6 flex items-center justify-center" role="status">
          <Loader2 className="w-4 h-4 text-rc-berry animate-spin" />
        </div>
      ) : rows.length === 0 ? (
        <p className="text-rc-slate text-[13px] italic mb-5">Noch kein Schlüssel erstellt.</p>
      ) : (
        <ul className="flex flex-col gap-2 mb-5">
          {rows.map((row) => {
            const st = keyState(row);
            return (
              <li key={row.id} className="flex items-center gap-3 px-3 py-2.5 rounded-xl border border-rc-fog">
                <KeyRound className={`w-4 h-4 shrink-0 ${st.live ? "text-rc-berry" : "text-rc-slate"}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-rc-black text-[14px] font-semibold truncate">
                    {row.label || "Ohne Namen"} <span className="font-mono text-rc-slate text-[12px]">…{row.key_hint}</span>
                  </div>
                  <div className="text-rc-slate text-[12px]">
                    {st.label}
                    {row.last_used_at ? ` · zuletzt genutzt ${formatDate(row.last_used_at)}` : ""}
                  </div>
                </div>
                {st.live && (
                  <button
                    type="button"
                    onClick={() => handleRevoke(row)}
                    aria-label={`Schlüssel …${row.key_hint} widerrufen`}
                    className="p-2 text-rc-slate hover:text-rc-berry hover:bg-rc-berry/8 rounded-full transition-colors"
                  >
                    <Ban className="w-4 h-4" />
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <form onSubmit={handleCreate} className="flex flex-col gap-3 pt-5 border-t border-rc-fog">
        <label className="text-[11px] font-semibold text-rc-charcoal uppercase tracking-[0.14em]">Neuer Schlüssel</label>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            maxLength={80}
            placeholder="z. B. Vortrag Hannover"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            className="flex-1 bg-rc-paper border-[1.5px] border-rc-fog rounded-xl px-3 py-2.5 text-[14px] text-rc-black placeholder:text-rc-slate/70 focus:outline-none focus:border-rc-berry focus:ring-2 focus:ring-rc-berry/15"
          />
          <div className="relative inline-flex">
            <select
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
              aria-label="Gültigkeit"
              className="appearance-none bg-rc-paper border-[1.5px] border-rc-fog rounded-xl pl-3 pr-8 py-2.5 text-[14px] text-rc-black font-semibold focus:outline-none focus:border-rc-berry focus:ring-2 focus:ring-rc-berry/15"
            >
              {VALIDITY_OPTIONS.map((o) => (
                <option key={o.days} value={o.days}>{o.label}</option>
              ))}
            </select>
            <ChevronDown className="w-4 h-4 absolute right-2 top-1/2 -translate-y-1/2 text-rc-slate pointer-events-none" />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="inline-flex items-center justify-center gap-2 bg-rc-berry text-rc-paper rounded-pill px-5 py-2.5 text-[14px] font-semibold hover:bg-rc-berry-700 disabled:opacity-50 transition active:scale-[0.97]"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
            Erstellen
          </button>
        </div>
        {error && <p className="text-rc-berry text-[12px] font-medium">{error}</p>}
      </form>
    </section>
  );
}
